import React, {Fragment, useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import CacheService from "../../../../../../../../core/services/cache/cache_service";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Card, Divider, FormControlLabel, Tooltip, Typography} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";
import {Switch} from "../../../../../../../components/base/switch";

export const NexusSearchConfigurationsSection = () => {
    const [loading, setLoading] = useState(false);
    const [configs, setConfigs] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Fetches the configurations data from the server
     * @type {(function(): Promise<void>)}
     */
    const getConfigurations = useCallback(async () => {
        setLoading(true);
        const response = await Api.getNexusSearchConfigurations();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setConfigs(response.data ?? {});
            CacheService.setNexusSearchConfigs({
                canToggleAdvancedMode: response?.data?.advancedSearchInOperator,
                canSeeAutocompleteSuggestions: response?.data?.showSuggestionInOperator,
                sidebarFacetsCollapsedByDefault: response?.data?.defaultCollapsedInOperator,
                hidePropertyGroupFacetMostFrequentValues: response?.data?.hideGroupPropMostFrequentItemsInOperator,
            });
        }
    }, [isMounted]);

    /**
     * As soon as the component is mounted:
     * - Fetches the configurations data from the server
     */
    useEffect(() => {
        void getConfigurations();
    }, [getConfigurations])

    /**
     * Updates the configuration value of the config record.
     * @type {(function(*, *): Promise<void>)}
     */
    const updateConfigValue = useCallback(async (name, value) => {
        const forApi = {
            ...configs,
            [name]: value,
        }
        setSubmitting(true);
        const response = await Api.updateNexusSearchConfig(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            setConfigs(forApi);
            const map = {
                advancedSearchInOperator: 'canToggleAdvancedMode',
                showSuggestionInOperator: 'canSeeAutocompleteSuggestions',
                defaultCollapsedInOperator: 'sidebarFacetsCollapsedByDefault',
                hideGroupPropMostFrequentItemsInOperator: 'hidePropertyGroupFacetMostFrequentValues',
            }
            if (map[name]) {
                CacheService.setNexusSearchConfigs({
                    canToggleAdvancedMode: configs.advancedSearchInOperator,
                    canSeeAutocompleteSuggestions: configs.showSuggestionInOperator,
                    sidebarFacetsCollapsedByDefault: configs.defaultCollapsedInOperator,
                    hidePropertyGroupFacetMostFrequentValues: configs.hideGroupPropMostFrequentItemsInOperator,
                    [map[name]]: value,

                });
            }
        }
    }, [configs, isMounted])


    return (
        <>
            {
                loading &&
                Array.from({length: 2}).map((_, index) => (
                    <Grid2 xs={12} key={index}>
                        <div className={'loading access-level w-100'}>
                            <div>
                                <div/>
                            </div>
                        </div>
                    </Grid2>
                ))
            }
            {
                !loading &&
                <>
                    <Grid2 xs={12}>
                        <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        Operator Panel Nexus Search Config
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                </Grid2>
                                {
                                    [
                                        ["Allow Advanced Search", "advancedSearchInOperator", "Whether the operator can toggle advanced search mode in the operator platform"],
                                        ["Show Autocomplete Suggestions", "showSuggestionInOperator", "Whether the operator can see autocomplete suggestions in the operator platform"],
                                        ["Sidebar Facets Collapsed by default", "defaultCollapsedInOperator", "Whether the sidebar facets are collapsed by default in the operator platform"],
                                        ["Hide Most Frequent Application Values in Sidebar", "hideGroupPropMostFrequentItemsInOperator", "Whether the most frequent values are shown in the facets in the operator platform"],
                                    ].map(([title, name, description]) => (
                                        <Grid2 key={name} xs={12} lg={6} paddingInline={3}>
                                            <FormControlLabel
                                                label={
                                                    <Fragment>
                                                        {title}
                                                        <Tooltip title={description}>
                                                            <InfoOutlined sx={{marginInlineStart: 1}} color={'primary'}/>
                                                        </Tooltip>
                                                    </Fragment>
                                                }
                                                sx={{margin: 0, width: '100%', justifyContent: 'space-between', alignItems: 'center'}}
                                                labelPlacement={'start'}
                                                control={
                                                    <Switch
                                                        disabled={submitting}
                                                        disableRipple
                                                        checked={configs[name]}
                                                        onChange={(val) => updateConfigValue(name, val)}
                                                    />
                                                }
                                            />
                                        </Grid2>
                                    ))
                                }
                            </Grid2>
                        </Card>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        Client Website Nexus Search Config
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                </Grid2>
                                {
                                    [
                                        ["Allow Advanced Search", "advancedSearchInClient", "Whether the user can toggle advanced search mode in the client website"],
                                        ["Show Autocomplete Suggestions", "showSuggestionInClient", "Whether the user can see autocomplete suggestions in the client website"],
                                        ["Sidebar Facets Collapsed by default", "defaultCollapsedInClient", "Whether the sidebar facets are collapsed by default in the client website"],
                                        ["Hide Most Frequent Application Values in Sidebar", "hideGroupPropMostFrequentItemsInClient", "Whether the most frequent values are shown in the facets in the client website"],
                                    ].map(([title, name, description]) => (
                                        <Grid2 key={name} xs={12} lg={6} paddingInline={3}>
                                            <FormControlLabel
                                                label={
                                                    <Fragment>
                                                        {title}
                                                        <Tooltip title={description}>
                                                            <InfoOutlined sx={{marginInlineStart: 1}} color={'primary'}/>
                                                        </Tooltip>
                                                    </Fragment>
                                                }
                                                sx={{margin: 0, width: '100%', justifyContent: 'space-between', alignItems: 'center'}}
                                                labelPlacement={'start'}
                                                control={
                                                    <Switch
                                                        disabled={submitting}
                                                        disableRipple
                                                        checked={configs[name]}
                                                        onChange={(val) => updateConfigValue(name, val)}
                                                    />
                                                }
                                            />
                                        </Grid2>
                                    ))
                                }
                            </Grid2>
                        </Card>
                    </Grid2>
                </>
            }
        </>
    )
}